import type {Method} from './internal';
import {TransformerIdsEnum} from './internal';

export enum ResultTypesEnum {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  EMAIL = 'email',
  URL = 'url',
  DATETIME = 'datetime',
  LIST = 'list',
  CONTACT = 'contact',
  ACCOUNT = 'account',
  TEAMMATE = 'teammate',
  INBOX = 'inbox',
  TAG = 'tag',
  TOPIC = 'topic',
  CONVERSATION = 'conversation',
  MESSAGE = 'message',
  COMMENT = 'comment',
  ACTIVITY = 'activity',
  AUTOLINKER = 'autolinker',
  PREVIEWER = 'previewer',
  CIRCLE = 'circle',
  JSON = 'json',
  DATA_TABLE = 'data_table',
  UNDEFINED = 'undefined',
  UNKNOWN = 'unknown',
}

export interface ResultType {
  name: ResultTypesEnum;
  properties: {[id: string]: InferredType};
  methods: Partial<Record<TransformerIdsEnum, Method>>;
  immediate?: ReadonlyArray<string>;
}

/*
 * Inferred type.
 */

interface InferredValueType {
  type: ResultTypesEnum;
}

interface InferredListType extends InferredValueType {
  type: ResultTypesEnum.LIST;
  itemType: InferredType;
}

export type InferredType = InferredValueType | InferredListType;

export function isListType(inferredType: InferredType): inferredType is InferredListType {
  return 'itemType' in inferredType && Boolean(inferredType.itemType);
}

export function buildListResultOf(itemType: InferredType) {
  return {type: ResultTypesEnum.LIST, itemType};
}

export function areInferredTypesEqual(inferredType1: InferredType, inferredType2: InferredType): boolean {
  if (isListType(inferredType1) && isListType(inferredType2)) {
    return areInferredTypesEqual(inferredType1.itemType, inferredType2.itemType);
  }

  return inferredType1.type === inferredType2.type;
}

export const StringResult: InferredType = {type: ResultTypesEnum.STRING};
export const StringListResult: InferredType = buildListResultOf(StringResult);
export const EmailResult: InferredType = {type: ResultTypesEnum.EMAIL};
export const EmailListResult: InferredType = buildListResultOf(EmailResult);
export const UrlResult: InferredType = {type: ResultTypesEnum.URL};
export const UrlListResult: InferredType = buildListResultOf(UrlResult);
export const NumberResult: InferredType = {type: ResultTypesEnum.NUMBER};
export const NumberListResult: InferredType = buildListResultOf(NumberResult);
export const DateTimeResult: InferredType = {type: ResultTypesEnum.DATETIME};
export const BooleanResult: InferredType = {type: ResultTypesEnum.BOOLEAN};
export const TeammateResult: InferredType = {type: ResultTypesEnum.TEAMMATE};
export const TeammateListResult: InferredType = buildListResultOf(TeammateResult);
export const ContactResult: InferredType = {type: ResultTypesEnum.CONTACT};
export const ContactListResult: InferredType = buildListResultOf(ContactResult);
export const AccountResult: InferredType = {type: ResultTypesEnum.ACCOUNT};
export const TagResult: InferredType = {type: ResultTypesEnum.TAG};
export const TagListResult: InferredType = buildListResultOf(TagResult);
export const TopicResult: InferredType = {type: ResultTypesEnum.TOPIC};
export const TopicListResult: InferredType = buildListResultOf(TopicResult);
export const InboxResult: InferredType = {type: ResultTypesEnum.INBOX};
export const InboxListResult: InferredType = buildListResultOf(InboxResult);
export const ConversationResult: InferredType = {type: ResultTypesEnum.CONVERSATION};
export const MessageResult: InferredType = {type: ResultTypesEnum.MESSAGE};
export const CommentResult: InferredType = {type: ResultTypesEnum.COMMENT};
export const ActivityResult: InferredType = {type: ResultTypesEnum.ACTIVITY};
export const AutolinkerResult: InferredType = {type: ResultTypesEnum.AUTOLINKER};
export const PreviewerResult: InferredType = {type: ResultTypesEnum.PREVIEWER};
export const CircleResult: InferredType = {type: ResultTypesEnum.CIRCLE};
export const JsonResult: InferredType = {type: ResultTypesEnum.JSON};
export const DataTableResult: InferredType = {type: ResultTypesEnum.DATA_TABLE};
export const JsonListResult: InferredType = buildListResultOf(JsonResult);
export const UnknownResult: InferredType = {type: ResultTypesEnum.UNKNOWN};

export const StringLikeResults = [StringResult, EmailResult, UrlResult, NumberResult];
